import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StaffMember } from '@acs/shared';
import { subscribeAvailability } from '../../shared/api/availability.js';
import type { AvailabilityDoc, ServiceDoc, StaffDoc } from '../../shared/api/converters.js';
import { keys } from '../../shared/api/keys.js';
import { subscribeServices } from '../../shared/api/services.js';
import {
  deleteStaffMember,
  reorderStaff,
  subscribeStaff,
  upsertStaffMember,
} from '../../shared/api/staff.js';
import { useLiveQuery } from '../../shared/api/useLiveQuery.js';

export function useStaff(businessId: string) {
  return useLiveQuery<StaffDoc[]>(keys.staff(businessId), (cb) => subscribeStaff(businessId, cb));
}

/** Services + availability, read by the staff editor (eligibility + fallback/ghosts). */
export function useServicesForStaff(businessId: string) {
  return useLiveQuery<ServiceDoc[]>(keys.services(businessId), (cb) => subscribeServices(businessId, cb));
}

export function useBusinessAvailability(businessId: string) {
  return useLiveQuery<AvailabilityDoc | null>(keys.availability(businessId), (cb) =>
    subscribeAvailability(businessId, cb),
  );
}

export function useUpsertStaffMember(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ staff, id }: { staff: StaffMember; id?: string }) =>
      upsertStaffMember(businessId, staff, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.staff(businessId) }),
  });
}

export function useDeleteStaffMember(businessId: string) {
  return useMutation({ mutationFn: (id: string) => deleteStaffMember(businessId, id) });
}

export function useReorderStaff(businessId: string) {
  return useMutation({ mutationFn: (orderedIds: string[]) => reorderStaff(businessId, orderedIds) });
}
